/**
 * Fetch a single self-schedule record by ID.
 * GET /api_ats/v1/ats_schedules/:id
 *
 * The shape overlaps `InterviewDetails` (name, interview_kind, location,
 * participants, meeting_rooms, timezone, comm bundle) plus the
 * self-schedule specific fields `time_slots` + `schedule_type`. Keep
 * the return type aligned with `InterviewDetails` so the shared
 * hydrator (`hydrateDetailsFromApi`) can consume either response
 * without branching.
 */
import { useQuery } from '@tanstack/react-query';
import { http } from '@/lib/ats-https';
import { asAtsClientBody, unwrapAtsEnvelope } from '@/api/ats/atsEnvelope';
import type { InterviewDetails } from '@/api/useInterviews';

export async function fetchSelfSchedule(
  scheduleId: number
): Promise<InterviewDetails> {
  const raw = asAtsClientBody<unknown>(
    await http.get(`/ats_schedules/${scheduleId}`)
  );
  return unwrapAtsEnvelope<InterviewDetails>(raw);
}

export function useGetSelfSchedule(
  scheduleId: number | undefined,
  options?: { enabled?: boolean }
) {
  return useQuery<InterviewDetails, Error>({
    queryKey: ['schedule-interview', 'self-schedule-detail', scheduleId],
    queryFn: () => fetchSelfSchedule(scheduleId!),
    enabled: (options?.enabled ?? true) && scheduleId !== undefined,
    // Mirror `useGetInterview` — always refetch on mount so the edit
    // drawer reflects the latest server state, even if cached.
    refetchOnMount: 'always',
    staleTime: 0,
  });
}
